import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { formatPeso, transferSources } from '@tahi/core';
import { deleteTransfer, transfer } from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { useGoBack } from '../../spending/hooks';
import { AccountChips } from '../components/AccountChips';
import { copy, errorText } from '../copy';
import { parsePesoInput } from '../format';
import { useAccounts, useToday } from '../hooks';

/** v0.5: move money between two accounts (GoTyme → GCash ₱1,000), with an optional fee. */
export function TransferRoute() {
  const env = useCommandEnv();
  const [sp] = useSearchParams();
  const accounts = useAccounts();
  const today = useToday();
  const goBack = useGoBack('/m/budget/accounts');
  const [picked, setFrom] = useState<string | null>(sp.get('from'));
  const [to, setTo] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (accounts === undefined) return null;
  // v0.6: a locked account (time deposit) can't be a source until its maturity date.
  const sources = transferSources(accounts, date);
  const from = picked !== null && sources.some((a) => a.id === picked) ? picked : null;
  const minor = parsePesoInput(amount);
  const feeMinor = fee.trim() === '' ? 0 : parsePesoInput(fee);
  const ready = from !== null && to !== null && minor !== null && feeMinor !== null;
  const nameOf = (id: string) => accounts.find((a) => a.id === id)?.name ?? '';

  async function save() {
    if (!ready || minor === null || feeMinor === null || !from || !to) return;
    setBusy(true);
    const r = await transfer(env, {
      fromAccountId: from,
      toAccountId: to,
      amountMinor: minor,
      feeMinor,
      date,
      note,
    });
    setBusy(false);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    showToast({
      message: copy.transferred(minor, nameOf(from), nameOf(to)),
      action: {
        label: copy.undo,
        onPress: () => void deleteTransfer(env, r.id),
      },
    });
    goBack();
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={copy.transferTitle}
      description={copy.transferNote}
      footer={
        <Button variant="primary" block disabled={!ready || busy} onClick={() => void save()}>
          {minor ? `${copy.transfer} ${formatPeso(minor, { cents: 'auto' })}` : copy.transfer}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <AccountChips
          label={copy.from}
          accounts={sources}
          value={from}
          clearable={false}
          onChange={(id) => {
            setFrom(id);
            if (id === to) setTo(null);
          }}
        />
        <AccountChips
          label={copy.to}
          accounts={accounts.filter((a) => a.id !== from)}
          value={to}
          clearable={false}
          onChange={setTo}
        />
        <TextField
          label={copy.amount}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          label={copy.fee}
          inputMode="decimal"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
        />
        <TextField
          label={copy.notePlaceholder}
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
        <TextField
          label={copy.dateLabel}
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
